import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { MicDeviceType } from "@/common/enums/enums";

export interface MicDevice {
  label: string;
  value: string;
  default: boolean;
  type: MicDeviceType;
}

export const useAudioInputDevices = (): {
  mics: MicDevice[];
  loading: boolean;
  refresh: () => Promise<void>;
} => {
  const [mics, setMics] = useState<MicDevice[]>([]);
  const [loading, setLoading] = useState(false);

  async function ensurePermission(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch {
      // soft catch
    }
  }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (!navigator?.mediaDevices?.enumerateDevices) {
        setMics([]);
        return;
      }
      const listInputs = async (): Promise<MediaDeviceInfo[]> =>
        (await navigator.mediaDevices.enumerateDevices()).filter(
          (d) => d.kind === "audioinput",
        );
      // Names are only listed once the microphone is allowed. Ask only then:
      // the probe opens the default microphone, which (a Bluetooth headset
      // switching profile) can fire devicechange and refresh the list again.
      let audioInputs = await listInputs();
      if (!audioInputs.length || audioInputs.some((d) => !d.label)) {
        await ensurePermission();
        audioInputs = await listInputs();
      }
      const options: MicDevice[] = audioInputs.map((d, index) => {
        return {
          default: index === 0,
          value: d.deviceId,
          label:
            d.label?.trim() ||
            t("components.advancedSettings.microphoneDevice.fallbackName", {
              number: index + 1,
            }),
          type: MicDeviceType.DESKTOP,
        };
      });
      setMics(options);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onChange = async (): Promise<void> => {
      await refresh();
    };
    const listener = (): void => {
      void onChange();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", listener);
    return (): void => {
      navigator.mediaDevices?.removeEventListener?.("devicechange", listener);
    };
  }, [refresh]);

  return { mics, loading, refresh };
};
